import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const CookiePolicy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Cookie Policy | BetterByAI</title>
        <meta name="description" content="Cookie Policy for BetterByAI - Learn about how we use cookies and similar technologies on our website." />
        <link rel="canonical" href="https://betterbyai.com/cookie-policy" />
      </Helmet>

      <div className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white/50 backdrop-blur-sm rounded-3xl border border-[#FF7066]/10 p-8 sm:p-10 shadow-lg"
          >
            <h1 className="text-4xl font-bold text-primary mb-8">Cookie Policy</h1>
            
            <div className="prose prose-lg max-w-none text-secondary">
              <p className="text-lg mb-6">
                Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">What Are Cookies?</h2>
              <p>
                Cookies are small text files that are placed on your device when you visit a website. They are widely used to make websites work more efficiently and provide information to the owners of the site. Cookies enhance user experience by allowing websites to remember your preferences and understand how you interact with their content.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">How We Use Cookies</h2>
              <p>BetterByAI uses cookies for the following purposes:</p>
              <ul className="list-disc pl-6 mb-6">
                <li><strong>Essential cookies:</strong> These cookies are necessary for the website to function properly. They enable basic functions like page navigation and access to secure areas of the website.</li>
                <li><strong>Analytics cookies:</strong> We use Google Analytics to collect anonymous information about how visitors use our website. These cookies help us understand which pages are most popular and how users navigate through the site, allowing us to improve your experience.</li>
                <li><strong>Preference cookies:</strong> These cookies allow our website to remember choices you make (such as your language preference or the region you are in) and provide enhanced, more personal features.</li>
                <li><strong>Marketing cookies:</strong> These cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user.</li>
              </ul>

              <h2 className="text-2xl font-bold mt-8 mb-4">Third-Party Cookies</h2>
              <p>
                Some cookies are placed by third parties on our website. These include:
              </p>
              <ul className="list-disc pl-6 mb-6">
                <li><strong>Google Analytics:</strong> We use Google Analytics to understand how visitors interact with our website. Google Analytics uses cookies to collect information about your visit to our website, including the pages you view, how long you spend on each page, and how you arrived at our site. <a href="https://policies.google.com/privacy" className="text-[#FF7066] hover:text-[#FF8A82] transition-colors" target="_blank" rel="noopener noreferrer">Learn more about Google's privacy practices</a>.</li>
              </ul>

              <h2 className="text-2xl font-bold mt-8 mb-4">Managing Your Cookie Preferences</h2>
              <p>
                When you first visit our website, you'll see a cookie banner that allows you to accept or decline non-essential cookies. You can change your preferences at any time by clearing your browser cookies and revisiting our site.
              </p>
              
              <p className="mt-4">
                Most web browsers also allow you to control cookies through their settings preferences. To find out more about cookies, including how to see what cookies have been set and how to manage and delete them, visit <a href="https://www.allaboutcookies.org" className="text-[#FF7066] hover:text-[#FF8A82] transition-colors" target="_blank" rel="noopener noreferrer">www.allaboutcookies.org</a>.
              </p>
              
              <div className="mt-6 p-4 bg-[#FF7066]/5 rounded-lg border border-[#FF7066]/10">
                <h3 className="text-xl font-semibold mb-2">How to control cookies in your browser</h3>
                <ul className="list-disc pl-6">
                  <li><strong>Chrome:</strong> <a href="https://support.google.com/chrome/answer/95647" className="text-[#FF7066] hover:text-[#FF8A82] transition-colors" target="_blank" rel="noopener noreferrer">Chrome cookie settings</a></li>
                  <li><strong>Firefox:</strong> <a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" className="text-[#FF7066] hover:text-[#FF8A82] transition-colors" target="_blank" rel="noopener noreferrer">Firefox cookie settings</a></li>
                  <li><strong>Safari:</strong> <a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" className="text-[#FF7066] hover:text-[#FF8A82] transition-colors" target="_blank" rel="noopener noreferrer">Safari cookie settings</a></li>
                  <li><strong>Edge:</strong> <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" className="text-[#FF7066] hover:text-[#FF8A82] transition-colors" target="_blank" rel="noopener noreferrer">Edge cookie settings</a></li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold mt-8 mb-4">Changes to Our Cookie Policy</h2>
              <p>
                We may update our Cookie Policy from time to time. We will notify you of any changes by posting the new Cookie Policy on this page and updating the "Last Updated" date.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">Contact Us</h2>
              <p>
                If you have any questions about our Cookie Policy, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-[#FF7066]/5 rounded-lg border border-[#FF7066]/10">
                <p><strong>Email:</strong> <a href="mailto:amrit@betterbyai.com" className="text-[#FF7066] hover:text-[#FF8A82] transition-colors">amrit@betterbyai.com</a></p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default CookiePolicy;